import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ChevronDown, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  loginWithOidc,
  loginWithPassword,
  startOidcRedirectFlow,
  completeOidcRedirect,
  AuthError,
} from "@/api/auth";
import { readPendingOidc, clearPendingOidc, parseCurrentCallback } from "@/lib/oidc";
import { fetchMe } from "@/api/me";
import { useAuthStore } from "@/store/useAuthStore";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/cn";
import { ApiClientError, UnauthenticatedError } from "@/api/client";

const OIDC_DEBUG = true;
function debug(...args: unknown[]) {
  if (OIDC_DEBUG) console.warn("[KORMAX-AUTH]", ...args);
}

interface LocationState {
  from?: Location;
}

function safeNextPath(state: LocationState | null): string {
  const from = state?.from;
  if (!from) return "/today";
  // Sanitize: only allow paths starting with / and not containing protocol/host.
  const p = from.pathname || "/today";
  if (!p.startsWith("/") || p.startsWith("//") || p === "/login") return "/today";
  return p + (from.search ?? "") + (from.hash ?? "");
}

/** Safe, user-facing message for an OAuth provider error/cancel. Never echoes raw HTML. */
function oauthErrorMessage(error: string, description?: string): string {
  if (error === "access_denied") {
    return "Вхід через Google скасовано. Спробуйте ще раз або скористайтесь паролем.";
  }
  return description || "Google повернув помилку входу. Спробуйте ще раз.";
}

/**
 * True when the popup-based OIDC flow is unreliable (mobile) and we should use the
 * full-page redirect instead. Desktop (incl. touch laptops, which have hover) keeps
 * the proven popup. Narrow viewport OR touch-primary (coarse pointer, no hover).
 */
function useFullPageOidc(): boolean {
  const isNarrow = useIsMobile(768);
  const isTouchPrimary =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse) and (hover: none)").matches;
  return isNarrow || isTouchPrimary;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const setCredentials = useAuthStore((s) => s.setCredentials);
  const setSession = useAuthStore((s) => s.setSession);
  const nextPath = safeNextPath(location.state as LocationState | null);
  const fullPageOidc = useFullPageOidc();

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [oidcLoading, setOidcLoading] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBusy = oidcLoading || pwLoading;

  /**
   * Atomic post-login: persist creds → drop any stale cached /me error from
   * the pre-login redirect → fetch fresh /me synchronously → seed query cache
   * + Zustand → navigate. ProtectedRoute on the destination route reads the
   * seeded cache (no refetch, no race with the old 401 verdict).
   */
  const onPostLoginSuccess = async (
    method: "password" | "oidc",
    userName: string,
    token: string,
  ) => {
    debug(`${method} login response received`, {
      has_userName: !!userName,
      userName_len: userName?.length ?? 0,
      has_token: !!token,
      token_len: token?.length ?? 0,
    });
    if (!userName || !token) {
      throw new AuthError(
        "unknown",
        "Сервер не повернув токен сесії. Спробуйте ще раз або скористайтесь паролем.",
      );
    }

    setCredentials({ userName, token });
    let lsProbe: string | null | "blocked" = null;
    try {
      lsProbe = localStorage.getItem("kormax-auth-creds");
    } catch {
      lsProbe = "blocked";
    }
    debug("credentials persisted", { ls: lsProbe ?? "absent" });

    // Clear cached pre-login 401 so ProtectedRoute on /today doesn't read a
    // stale error before the refetch settles. removeQueries drops the cache
    // entry entirely.
    queryClient.removeQueries({ queryKey: ["me"] });

    let me;
    try {
      me = await fetchMe();
      debug(`/me fetch succeeded`, {
        user: me.user?.name,
        roles: me.user?.roles?.length ?? 0,
        nav: me.visibleNavigation?.length ?? 0,
      });
    } catch (e) {
      const status =
        e instanceof ApiClientError ? e.status : e instanceof UnauthenticatedError ? 401 : -1;
      debug(`/me fetch FAILED`, {
        status,
        name: (e as Error)?.name,
        message: (e as Error)?.message,
      });
      // Roll back so ProtectedRoute doesn't think we're authed.
      useAuthStore.getState().reset();
      throw new AuthError(
        "unknown",
        "Вхід пройшов, але сесія не активувалась (запит /me повернув помилку). Спробуйте ще раз.",
      );
    }

    setSession(me);
    queryClient.setQueryData(["me"], me);
    debug("session seeded; navigating", { to: nextPath });
    navigate(nextPath, { replace: true });
  };

  /**
   * On-load: handle a MOBILE full-page redirect return. The custom oauth-callback
   * EntryPoint forwards Google's result to /login?code=&state= (or ?error=). We:
   *  (a) error → safe message + clean URL, do NOT proceed;
   *  (b) code+state → CSRF state-verify vs sessionStorage → complete via App/user.
   * Clean the URL on BOTH success and failure (history.replaceState — no code/state
   * left in the address bar/history). Everything is wrapped so any failure leaves the
   * password form usable (no lockout). The password form below is already painted by
   * the time this effect runs (fail-safe render).
   */
  const processedRef = useRef(false);
  useEffect(() => {
    if (processedRef.current) return;

    const cleanUrl = () => {
      try {
        window.history.replaceState({}, "", "/login");
      } catch {
        /* ignore */
      }
    };

    try {
      const cb = parseCurrentCallback();
      if (!cb || (!cb.code && !cb.error)) return; // normal load — nothing to complete
      processedRef.current = true;

      // (a) OAuth error / cancel → message, clean URL, keep form. No proceed.
      if (cb.error) {
        clearPendingOidc();
        cleanUrl();
        setError(oauthErrorMessage(cb.error, cb.errorDescription));
        return;
      }

      // (b) code + state → CSRF state-verify against the persisted state.
      const pending = readPendingOidc();
      if (!cb.state || !pending || pending.state !== cb.state) {
        clearPendingOidc();
        cleanUrl();
        setError("Сесію входу не вдалося підтвердити (state). Спробуйте увійти ще раз.");
        return;
      }

      const code = cb.code as string;
      const nonce = pending.nonce;
      setOidcLoading(true);
      void (async () => {
        try {
          debug("mobile redirect return: completing OIDC");
          const res = await completeOidcRedirect(code, nonce);
          clearPendingOidc();
          cleanUrl();
          await onPostLoginSuccess("oidc", res.user.userName, res.token);
        } catch (e) {
          clearPendingOidc();
          cleanUrl();
          debug("mobile redirect completion failed", {
            name: (e as Error)?.name,
            message: (e as Error)?.message,
          });
          if (e instanceof AuthError) {
            if (e.kind !== "user_closed") setError(e.message);
          } else {
            setError("Не вдалося завершити вхід через Google. Спробуйте ще раз.");
          }
        } finally {
          setOidcLoading(false);
        }
      })();
    } catch (e) {
      // Defensive: any parse/logic failure must not block the form.
      clearPendingOidc();
      cleanUrl();
      debug("on-load callback handler threw", { message: (e as Error)?.message });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogle = async () => {
    if (isBusy) return;
    setError(null);
    setOidcLoading(true);
    try {
      if (fullPageOidc) {
        // MOBILE: persist state/nonce + navigate the whole tab to Google. Does not
        // return — the page unloads. If it throws (network/storage), fall through to
        // the catch and the form stays usable.
        debug("Google login: mobile full-page redirect");
        await startOidcRedirectFlow();
        return;
      }
      debug("Google login: opening OIDC popup (desktop)");
      const res = await loginWithOidc();
      debug("OIDC code exchanged successfully", {
        body_keys: Object.keys(res),
        user_userName: res.user?.userName,
        user_name: res.user?.name,
      });
      await onPostLoginSuccess("oidc", res.user.userName, res.token);
    } catch (e) {
      debug("OIDC login failed", { name: (e as Error)?.name, message: (e as Error)?.message });
      if (e instanceof AuthError) {
        if (e.kind !== "user_closed") setError(e.message);
      } else {
        setError("Невідома помилка. Спробуйте ще раз.");
      }
    } finally {
      setOidcLoading(false);
    }
  };

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (isBusy) return;
    setError(null);
    if (!username.trim() || !password) {
      setError("Введіть логін і пароль.");
      return;
    }
    setPwLoading(true);
    try {
      const res = await loginWithPassword(username.trim(), password);
      await onPostLoginSuccess("password", res.user.userName, res.token);
    } catch (e) {
      if (e instanceof AuthError) setError(e.message);
      else setError("Невідома помилка. Спробуйте ще раз.");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        background:
          "radial-gradient(1100px 560px at 50% -10%, hsl(var(--card)) 0%, hsl(var(--muted)) 100%)",
      }}
    >
      <div className="w-full max-w-[420px]">
        <div className="rounded-[20px] border border-border bg-card px-7 py-9 text-center shadow-[0_1px_2px_rgba(16,36,43,0.05),0_14px_34px_-10px_rgba(16,36,43,0.12)] sm:px-9">
          {/* Logo tile — real Kormax AVA mark (petrol #005679 + green #00915d) */}
          <div className="mx-auto mb-5 grid h-[84px] w-[84px] place-items-center rounded-[18px] border border-border bg-white shadow-sm">
            <KormaxAvaLogo className="h-[52px] w-[52px]" />
          </div>

          <h1 className="text-[26px] font-bold tracking-tight text-foreground">
            Kormax <span className="text-primary">CRM</span>
          </h1>
          <p className="mt-1.5 text-[15px] text-muted-foreground">Робочий простір</p>

          <div className="my-6 h-px bg-border" />

          {error && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-left text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {/* Primary CTA — outlined Google (redirect on mobile, popup on desktop) */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogle}
            disabled={isBusy}
            className="h-12 w-full gap-3 text-[15px] font-semibold"
          >
            {oidcLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Зачекайте…
              </>
            ) : (
              <>
                <GoogleIcon className="h-5 w-5" />
                Увійти через Google
              </>
            )}
          </Button>

          {/* Fallback toggle */}
          <button
            type="button"
            onClick={() => {
              setShowPasswordForm((v) => !v);
              setError(null);
            }}
            disabled={isBusy}
            aria-expanded={showPasswordForm}
            className={cn(
              "mt-3.5 inline-flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isBusy && "cursor-not-allowed opacity-50",
            )}
          >
            Інший спосіб входу
            <ChevronDown
              className={cn("h-3.5 w-3.5 transition-transform", showPasswordForm && "rotate-180")}
            />
          </button>

          {showPasswordForm && (
            <form onSubmit={handlePassword} className="mt-2 space-y-3 border-t border-border pt-4 text-left">
              <div>
                <label htmlFor="login-username" className="mb-1 block text-xs font-medium text-foreground">
                  Логін або email
                </label>
                <Input
                  id="login-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isBusy}
                  required
                />
              </div>
              <div>
                <label htmlFor="login-password" className="mb-1 block text-xs font-medium text-foreground">
                  Пароль
                </label>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isBusy}
                  required
                />
              </div>
              <Button type="submit" disabled={isBusy} className="h-11 w-full gap-2">
                {pwLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Перевіряємо…
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4" />
                    Увійти з паролем
                  </>
                )}
              </Button>
              <p className="text-center text-xs leading-relaxed text-muted-foreground">
                Співробітники входять через Google; пароль — лише для адміністраторів.
              </p>
            </form>
          )}

          <div className="mt-6 flex items-center justify-center gap-2 text-[13.5px] font-medium text-primary">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            Доступ лише для співробітників Kormax
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Захищений вхід через корпоративний обліковий запис Google.
          </p>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          Проблеми з входом? Зверніться до{" "}
          <a href="mailto:support@kormaxtrade.com.ua" className="text-primary hover:underline">
            адміністратора CRM
          </a>
          .
        </p>
      </div>
    </div>
  );
}

/** Real Kormax "AVA" mark (tz/Kormax-Logo-AVA.svg) — petrol #005679 + green #00915d. */
function KormaxAvaLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 590.968 556.73"
      className={className}
      role="img"
      aria-label="Kormax"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#005679"
        d="M160.749,108.305c-34.96,42.312-50.518,89.984-36.571,144.479,15.017,58.677,62.821,96.286,119.985,95.402,15.097-.233,29.924-2.598,45.127-9.061-1.981-.616-2.5-.898-3.032-.926-85.365-4.399-142.521-85.828-117.552-167.491,20.237-66.187,82.162-113.909,153.638-118.779,62.939-4.289,120.913,7.727,169.812,50.476.521-.562,1.042-1.123,1.562-1.685-7.241-6.834-14.205-13.991-21.763-20.453C380.076,1.716,238.283,14.464,160.749,108.305Z"
      />
      <path
        fill="#005679"
        d="M238.6,481.698c60.918,5.069,110.784-18.919,142.964-72.544,31.414-52.348,18.359-116.852-30.996-156.897,1.837,4.197,2.631,6.03,3.44,7.857,30.684,69.292,1.794,145.609-67.439,174.118-47.949,19.745-95.3,14.131-140.089-10.851-42.686-23.808-69.439-61.613-88.117-105.645-15.085-35.562-21.936-72.472-15.701-111.136.541-3.356,1.106-6.709,1.659-10.063-2.052,1.667-2.871,3.521-3.45,5.448-14.257,47.42-12.199,94.627,6.437,140.006,34.985,85.192,100.246,132.131,191.291,139.706Z"
      />
      <path
        fill="#00915d"
        d="M169.476,407.302c79.003,34.244,162.097-9.791,175.869-93.459-1.069.597-1.404.675-1.554.885-.849,1.192-1.629,2.433-2.477,3.625-20.492,28.786-47.896,47.689-82.469,54.711-56.83,11.542-112.768-16.206-142.02-69.25-23.5-42.612-28.847-87.514-14.282-133.8,18.918-60.121,54.741-107.523,110.567-138.382,9.422-5.208,19.886-8.53,29.869-12.724-11.052,1.678-21.937,4.085-32.346,7.819C103.634,65.113,43.92,183.581,75.837,293.639c14.895,51.362,43.11,91.761,93.639,113.663Z"
      />
      <path
        fill="#00915d"
        d="M423.071,455.235c39.541-43.144,57.726-93.125,43.162-151.789-13.621-54.866-55.302-90.458-111.123-95.236-18.202-1.558-35.881,1.681-54.944,8.742,8.29,1.106,14.374,1.732,20.391,2.749,61.254,10.351,107.827,64.522,106.833,126.535-.89,55.471-27.799,97.726-72.819,128.13-47.006,31.746-99.787,35.963-154.193,26.973-39.182-6.475-74.693-21.46-103.743-49.696-.561.672-1.121,1.344-1.682,2.015,11.152,10.096,21.535,21.235,33.579,30.123,91.876,67.796,217.312,55.719,294.539-28.546Z"
      />
      <path
        fill="#00915d"
        d="M516.948,166.216c-51.236-69.604-121.376-102.05-208.278-89.045-63.724,9.536-112.757,63.626-116.373,124.233-2.471,41.418,14.917,74.25,45.789,100.751-.235-2.323-1.15-4.347-2.048-6.377-29.777-67.342-2.538-142.367,64.021-172.272,45.768-20.563,92.305-17.494,136.166,5.503,66.133,34.675,97.894,94.007,111.404,164.734,4.492,23.521,3.231,47.279-3.848,72.797,1.907-2.818,2.56-3.481,2.858-4.278,1.024-2.739,2.061-5.486,2.853-8.298,19.063-67.766,8.801-131.582-32.543-187.748Z"
      />
      <path
        fill="#005679"
        d="M457.693,171.346c-45.07-35.668-95.127-45.722-148.39-20.231-36.783,17.603-55.845,49.596-62.297,89.195,2.65-3.461,5.242-6.969,7.96-10.376,47.307-59.296,131.281-66.414,187.76-15.929,47.821,42.746,66.019,115.185,43.301,178.011-24.772,68.505-67.932,120.099-138.404,145.793,10.727-2.291,21.558-4.664,31.913-8.368,96.938-34.67,157.426-137.231,141.005-239.311-7.496-46.595-24.83-88.698-62.847-118.784Z"
      />
    </svg>
  );
}

/** Google "G" — 4-colour mark for the outlined (white) button. */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
